import React, { useState, useEffect } from 'react';

const UniversalHeartToggle = ({ className = "" }) => {
  const [heartbeatEnabled, setHeartbeatEnabled] = useState(true);
  const [sonicEnabled, setSonicEnabled] = useState(false);

  // Initialize audio systems with force start
  useEffect(() => {
    // Force initialize and start heartbeat
    if (window.planetaryFedHeartbeat) {
      window.planetaryFedHeartbeat.setEnabled(true);
      
      // Force start heartbeat after brief delay
      setTimeout(() => {
        if (window.planetaryFedHeartbeat && !window.planetaryFedHeartbeat.heartbeatActive) {
          console.log('🫀 FORCE START: Initializing heartbeat from toggle component');
          window.planetaryFedHeartbeat.forceRestart();
        }
      }, 1000);
    }
    
    // Initialize global sonic
    if (window.globalSonicAudio) {
      window.globalSonicAudio.setEnabled(sonicEnabled);
    }
  }, []);

  const toggleAudio = () => {
    const newAudioState = !heartbeatEnabled;
    setHeartbeatEnabled(newAudioState);
    setSonicEnabled(newAudioState);
    
    // ENHANCED: Control ALL audio systems including persistent sonic experience
    if (window.planetaryFedHeartbeat) {
      window.planetaryFedHeartbeat.setEnabled(newAudioState);
      if (newAudioState) {
        window.planetaryFedHeartbeat.forceRestart();
      }
    }
    
    // ENHANCED: Control persistent sonic experience
    if (window.planetaryFedSonicExperience && window.planetaryFedSonicExperience.audioEngine) {
      window.planetaryFedSonicExperience.audioEngine.setGlobalMute(!newAudioState);
    }
    
    // Control other audio systems
    if (window.globalSonicAudio) {
      window.globalSonicAudio.setEnabled(newAudioState);
    }
    
    if (window.grantSpiralAudio) {
      window.grantSpiralAudio.setEnabled(newAudioState);
    }
    
    // ENHANCED: Universal audio system control
    const allAudioSystems = [
      window.planetaryFedHeartbeat,
      window.globalSonicAudio,
      window.grantSpiralAudio,
      window.tibetanBowlAudio,
      window.planetaryFedSonicExperience?.audioEngine
    ];
    
    allAudioSystems.forEach(audioSystem => {
      if (audioSystem && typeof audioSystem.setEnabled === 'function') {
        audioSystem.setEnabled(newAudioState);
      } else if (audioSystem && typeof audioSystem.setGlobalMute === 'function') {
        audioSystem.setGlobalMute(!newAudioState);
      }
    });
    
    console.log('🎵 UNIVERSAL AUDIO CONTROL:', {
      all_audio_enabled: newAudioState,
      heartbeat: newAudioState,
      persistent_sonic: newAudioState,
      tuvan_throat_singing: newAudioState,
      harmonium_drones: newAudioState,
      systems_controlled: allAudioSystems.filter(s => s).length
    });
  };

  return (
    <div className={`fixed z-50 ${className}`} style={{ bottom: '8px', left: '24px' }}>
      <button
        onClick={toggleAudio}
        className={`text-lg hover:scale-125 transition-all duration-300 p-1 border-none outline-none opacity-90 hover:opacity-100 ${
          heartbeatEnabled ? 'animate-pulse' : ''
        }`}
        style={{ 
          background: 'transparent',
          border: 'none',
          filter: heartbeatEnabled 
            ? 'drop-shadow(0 0 6px rgba(255, 20, 147, 0.8)) drop-shadow(0 0 12px rgba(255, 105, 180, 0.6))'
            : 'drop-shadow(0 0 4px rgba(0, 0, 0, 0.8))',
          animation: heartbeatEnabled ? 'anatomical-heartbeat 1.2s ease-in-out infinite' : 'none'
        }}
        title={`${heartbeatEnabled ? 'Mute all audio' : 'Enable all audio'} (heartbeat + sonic tones)`}
      >
        <span style={{ 
          filter: heartbeatEnabled ? 'brightness(1.2) saturate(1.4)' : 'brightness(0.6)',
          fontSize: '0.7em',
          display: 'inline-block',
          animation: heartbeatEnabled ? 'anatomical-heartbeat 1.2s ease-in-out infinite' : 'none',
          transform: heartbeatEnabled ? 'scale(1.0)' : 'scale(0.9)'
        }}>
          🫀
        </span>
      </button>
      
      {/* Reduced size pulsing indicator */}
      {heartbeatEnabled && (
        <div className="absolute -top-0.5 -right-0.5">
          <div className="w-2 h-2 bg-red-400 rounded-full animate-ping opacity-75"></div>
          <div className="absolute top-0 left-0 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
        </div>
      )}
      
      {/* Enhanced heartbeat pulse animation with intensity */}
      <style jsx>{`
        @keyframes anatomical-heartbeat {
          0%, 100% { 
            transform: scale(1.1); 
            filter: brightness(1.2) saturate(1.4) drop-shadow(0 0 8px rgba(255, 20, 147, 0.6));
          }
          15% { 
            transform: scale(1.3); 
            filter: brightness(1.5) saturate(1.8) drop-shadow(0 0 16px rgba(255, 20, 147, 0.9));
          }
          30% { 
            transform: scale(1.15); 
            filter: brightness(1.3) saturate(1.5) drop-shadow(0 0 12px rgba(255, 20, 147, 0.7));
          }
          45% { 
            transform: scale(1.25); 
            filter: brightness(1.4) saturate(1.7) drop-shadow(0 0 14px rgba(255, 20, 147, 0.8));
          }
          60% { 
            transform: scale(1.1); 
            filter: brightness(1.2) saturate(1.4) drop-shadow(0 0 8px rgba(255, 20, 147, 0.6));
          }
        }
      `}</style>
    </div>
  );
};

export default UniversalHeartToggle;